import { useContext, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { createLanguage, findAllLanguages } from "@/api/query/language";
import { createJobProcess } from "@/api/query/jobProcess";
import { MessageContext } from "@/common/contexts/message.context";
import { getBase64 } from "@/common/functions/getBase64";
import {
  DELIMITER_OPTIONS,
  LANGUAGE_MODE,
  WIZARD_STEP,
} from "../constants";
import {
  buildCreateLanguagePayload,
  buildDuplicateLessonsJob,
  buildImportDatabaseJob,
  buildMarkWordsAsUserJob,
  getJobErrorMessage,
  hasIncompleteVoice,
  mapLanguagesToOptions,
} from "../helpers";

const useAddLanguageWizard = ({ setClose, reloadMutation }) => {
  const [step, setStep] = useState(WIZARD_STEP.ADD_LANGUAGE);
  const [languageMode, setLanguageMode] = useState();
  const [languagesOptions, setLanguagesOptions] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState();
  const [createdLanguage, setCreatedLanguage] = useState();
  const [selectedDelimiter, setSelectedDelimiter] = useState(
    DELIMITER_OPTIONS[0]
  );
  const [voices, setVoices] = useState([]);

  const nameRef = useRef();
  const shortNameRef = useRef();
  const isLangToRef = useRef();
  const csvFileRef = useRef();

  const { addMessage } = useContext(MessageContext);

  const targetLanguage = createdLanguage || selectedExisting?.value;

  const loadLanguagesMutation = useMutation({
    mutationFn: () => findAllLanguages(),
    onSuccess: ({ data }) => {
      setLanguagesOptions(mapLanguagesToOptions(data));
    },
  });

  const createLanguageMutation = useMutation({
    mutationFn: (value) => createLanguage(value),
    onSuccess: ({ data }) => {
      setCreatedLanguage(data);
      addMessage("Language created", "success");
      setStep(WIZARD_STEP.COPY_LESSONS);
      loadLanguagesMutation.mutate();
    },
    onError: () => {
      addMessage("Could not create language", "error");
    },
  });

  const createJobProcessMutation = useMutation({
    mutationFn: (values) => createJobProcess(values),
    onSuccess: () => {
      addMessage("Job queued — wait until it ends in the table", "success");
      reloadMutation.mutate();
    },
    onError: (error) => {
      addMessage(getJobErrorMessage(error), "error");
    },
  });

  const handleClose = () => {
    setClose((prev) => !prev);
  };

  const handleStepOneBack = () => {
    if (languageMode) {
      setLanguageMode(null);
      setSelectedExisting(null);
      return;
    }

    handleClose();
  };

  const handleSelectExistingLanguage = () => {
    if (!selectedExisting?.value) {
      addMessage("Select a language", "error");
      return;
    }

    setCreatedLanguage(selectedExisting.value);
    setStep(WIZARD_STEP.COPY_LESSONS);
  };

  const handleCreateLanguage = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (languageMode === LANGUAGE_MODE.SELECT) {
      handleSelectExistingLanguage();
      return;
    }

    const hasMissingFields =
      !nameRef.current?.value || !shortNameRef.current?.value;

    if (hasMissingFields) {
      addMessage("Fill language name and short name", "error");
      return;
    }

    if (hasIncompleteVoice(voices)) {
      addMessage("Fill voice config", "error");
      return;
    }

    createLanguageMutation.mutate(
      buildCreateLanguagePayload({
        name: nameRef.current.value,
        shortName: shortNameRef.current.value,
        isLangTo: isLangToRef.current.checked,
        voices,
      })
    );
  };

  const handleDuplicateLessons = () => {
    if (!targetLanguage) {
      addMessage("Select or create a language first", "error");
      return;
    }

    createJobProcessMutation.mutate(
      buildDuplicateLessonsJob(targetLanguage.shortName)
    );
  };

  const handleImportDatabase = async () => {
    if (!targetLanguage) {
      addMessage("Select or create a language first", "error");
      return;
    }

    const file = csvFileRef.current?.files?.[0];

    if (!file) {
      addMessage("Upload CSV file", "error");
      return;
    }

    const fileBase64 = await getBase64(file);

    createJobProcessMutation.mutate(
      buildImportDatabaseJob({
        langShort: targetLanguage.shortName,
        csvDelimiter: selectedDelimiter?.value || ",",
        fileBase64,
      })
    );
  };

  const handleMarkWordsAsUser = () => {
    if (!targetLanguage) {
      addMessage("Select or create a language first", "error");
      return;
    }

    createJobProcessMutation.mutate(
      buildMarkWordsAsUserJob(targetLanguage.shortName)
    );
  };

  useEffect(() => {
    loadLanguagesMutation.mutate();
  }, []);

  const isLoading =
    createLanguageMutation.isLoading ||
    createJobProcessMutation.isLoading ||
    loadLanguagesMutation.isLoading;

  return {
    step,
    setStep,
    languageMode,
    setLanguageMode,
    languagesOptions,
    selectedExisting,
    setSelectedExisting,
    selectedDelimiter,
    setSelectedDelimiter,
    voices,
    setVoices,
    nameRef,
    shortNameRef,
    isLangToRef,
    csvFileRef,
    targetLanguage,
    isLoading,
    handleClose,
    handleStepOneBack,
    handleCreateLanguage,
    handleDuplicateLessons,
    handleImportDatabase,
    handleMarkWordsAsUser,
  };
};

export default useAddLanguageWizard;
